import { useState, useMemo } from 'react';
import useResizeObserver from 'use-resize-observer/polyfilled';
import { formatDistance } from 'date-fns';
import { debounce } from 'throttle-debounce';
import navigatorLanguages from 'navigator-languages';
import Autolinker from 'autolinker';

import { i18n } from '@lingui/core';
import { networks, DATE_FNS_LOCALES, LOCALES } from './consts';
import variables from './styles/variables.module.scss';

const DARK_SWAP_VALUE = 0.179;
const SHADE_PERCENT = 6.5;
export function ensureURLProtocol(url) {
  if (!/^https?:\/\//i.test(url)) {
    return `//${url}`;
  }

  return url;
}

export const NetworksCommonName = {
  [networks.TWITTER]: networks.TWITTER,
  [networks.INSTAGRAM]: networks.INSTAGRAM,
  [networks.INSTAGRAM_GRAPH]: networks.INSTAGRAM,
  [networks.YOUTUBE]: networks.YOUTUBE,
  [networks.CUSTOM]: networks.CUSTOM,
  [networks.FACEBOOK]: networks.FACEBOOK,
  [networks.OPEN_GRAPH]: networks.OPEN_GRAPH,
  [networks.GIGG]: networks.GIGG,
  [networks.TIK_TOK]: networks.TIK_TOK,
  [networks.RSS]: networks.RSS,
};

export const NETWORK_LABELS = {
  [networks.INSTAGRAM]: 'instagram',
  [networks.INSTAGRAM_GRAPH]: 'instagram',
  [networks.YOUTUBE]: 'youtube',
  [networks.TWITTER]: 'twitter',
  [networks.RSS]: 'rss_feeds',
  [networks.CUSTOM]: '',
};

function calculateLValue(passedColor) {
  const color = passedColor.charAt(0) === '#' ? passedColor.substring(1, 7) : passedColor;
  const r = parseInt(color.substring(0, 2), 16); // hexToR
  const g = parseInt(color.substring(2, 4), 16); // hexToG
  const b = parseInt(color.substring(4, 6), 16); // hexToB
  const uicolors = [r / 255, g / 255, b / 255];
  const c = uicolors.map((col) => {
    if (col <= 0.03928) {
      return col / 12.92;
    }
    return ((col + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

export function contrastTextColor(bgColor) {
  const realBgColor = bgColor || variables.slideDark;

  return calculateLValue(realBgColor) > DARK_SWAP_VALUE
    ? variables.textColorDark
    : variables.textColorPrimary;
}

export function shadeColor(passedColor) {
  if (!passedColor) {
    return variables.slideDark;
  }
  const lValue = calculateLValue(passedColor);
  const percent = lValue > DARK_SWAP_VALUE ? SHADE_PERCENT * -1 : SHADE_PERCENT;

  const color = passedColor.charAt(0) === '#' ? passedColor.substring(1, 7) : passedColor;
  let R = parseInt(color.substring(0, 2), 16);
  let G = parseInt(color.substring(2, 4), 16);
  let B = parseInt(color.substring(4, 6), 16);

  R = parseInt(((R * (100 + percent)) / 100).toString(), 10);
  G = parseInt(((G * (100 + percent)) / 100).toString(), 10);
  B = parseInt(((B * (100 + percent)) / 100).toString(), 10);

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  if (R === 0 && G === 0 && B === 0) {
    R = 17;
    G = 17;
    B = 17;
  }

  const RR = R.toString(16).length === 1 ? `0${R.toString(16)}` : R.toString(16);
  const GG = G.toString(16).length === 1 ? `0${G.toString(16)}` : G.toString(16);
  const BB = B.toString(16).length === 1 ? `0${B.toString(16)}` : B.toString(16);
  return `#${RR}${GG}${BB}`;
}

export function trimString(string = '', length, ending = '...') {
  if (string.length > length) {
    return string.substring(0, length - ending.length) + ending;
  }
  return string;
}

function parseHashtagsManually(text) {
  return text.replace(/#(\w+)/g, '<a href="https://www.youtube.com/hashtag/$1">#$1</a>');
}

const LINKABLE_SERVICES = new Set([networks.TWITTER, networks.INSTAGRAM]);
export function formatText(text, { service, color, disableLinks = false }) {
  const network = NetworksCommonName[service];
  const showLinks = !disableLinks;

  const options = {
    urls: showLinks,
  };

  if (showLinks && LINKABLE_SERVICES.has(network)) {
    options.mention = network;
    options.hashtag = network;
  }

  let linkedText = Autolinker.link(text, options);

  if (network === 'youtube') {
    linkedText = parseHashtagsManually(linkedText);
  }

  return {
    __html: linkedText.replace(
      /<a/g,
      `<a style="color:${color}; -webkit-text-fill-color:${color}" role="link"`
    ),
  };
}
async function fetchJSON(url, options) {
  return fetch(url, options).then((response) => response.json());
}

export async function fetchOptions(url) {
  return fetchJSON(url);
}

export async function fetchPost(boardId, postId) {
  return fetchJSON(
    `${process.env.REACT_APP_CORE_API_URL}/public/boards/${boardId}/posts/${postId}`,
    {
      headers: {
        Accept: 'application/json',
      },
    }
  );
}

export function fetchPostsArray(url, firstPage, pages, searchParams) {
  const requests = [];
  for (let index = firstPage; index < pages + 1; index += 1) {
    requests.push(fetchJSON(`${url}/${index}?${searchParams}`).then((json) => json.posts));
  }
  return Promise.all(requests).then((posts) => [].concat(...posts));
}

export function initialFetch(url, searchParams) {
  return fetchJSON(`${url}/1?${searchParams}`);
}

export async function postApi(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  return response.json();
}

export function postKey(post) {
  return `${post.service}_${post.id}`;
}

export function isEmptyOrSpaces(str) {
  return str === null || str.match(/^ *$/) !== null;
}

export function testImage(media, callback) {
  const url = media[0]?.large;
  const tester = new Image();
  tester.addEventListener('error', callback);
  tester.src = url;
}

export const useDebouncedResizeObserver = (wait) => {
  const [size, setSize] = useState({});
  const onResize = useMemo(() => debounce(wait, setSize), [wait]);
  const { ref } = useResizeObserver({ onResize });

  return { ref, ...size };
};

export const getMediaType = (media) => {
  let mediaType = 'text';
  if (media.length && media[0]) {
    if (media[0].video) {
      mediaType = 'video';
    } else if (media[0].large) {
      mediaType = 'image';
    }
  }
  return mediaType;
};
export const openPostOnRedirect = async (posts, openModal, getReferredPost, config, embedType) => {
  const { getCurrentHashPost } = await import('./hashUtils');
  let postId = getCurrentHashPost();

  if (!postId) {
    const urlSearchParams = new URLSearchParams(document.location.search);
    postId = urlSearchParams.get('giggReferringItemId');
  }

  if (!postId) {
    return;
  }

  const postPosition = posts.map((post) => post.id).indexOf(postId);
  const post = posts[postPosition];
  if (!post) {
    const apiUrl = `${process.env.REACT_APP_CORE_API_URL}/public/boards/${config.accountId}/posts/${postId}`;
    const postResponse = await fetch(apiUrl);
    const postResult = await postResponse.json();
    const postDataWithId = {
      id: postId,
      ...postResult?.data,
    };
    openModal(postPosition, postDataWithId, embedType);
    getReferredPost(postDataWithId);
    return;
  }
  openModal(postPosition, post, embedType);
};

export const formatDistanceLocaleFunc = () => {
  const preferredLanguages = navigatorLanguages();
  const currentLocale = preferredLanguages.find((lang) => LOCALES.includes(lang)) || 'en';
  const locale = DATE_FNS_LOCALES[currentLocale];
  return (from, to) => formatDistance(from, to, { locale });
};

export async function dynamicI18nActivate(locale) {
  const { default: localeData } = await import(`./locales/${locale}/messages.js`);
  i18n.load(locale, localeData.messages);
  i18n.activate(locale);
}

export function appendUrlParams(url, params) {
  const urlObj = new URL(url);
  Object.keys(params).forEach((key) => {
    urlObj.searchParams.append(key, params[key]);
  });
  return urlObj.toString();
}
